import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl border border-slate-200" data-testid="forgot-password-page">
        {!sent ? (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail size={24} className="text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Forgot your password?</h2>
              <p className="text-slate-600 mt-1">
                Enter your email and we'll send you a link to reset it.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="forgot-password-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                disabled={submitting}
                data-testid="forgot-password-submit-button"
              >
                {submitting ? 'Sending…' : 'Send Reset Link'}
              </Button>
            </form>
            <Link
              to="/login"
              className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mt-6"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={14} />
              Back to login
            </Link>
          </>
        ) : (
          <div className="text-center py-4" data-testid="forgot-password-sent-state">
            <CheckCircle size={40} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check your inbox</h2>
            <p className="text-slate-600 mb-6">
              If <strong>{email}</strong> is registered, a password reset link is on its way. The link expires in 1 hour.
            </p>
            <Link to="/login">
              <Button variant="outline" data-testid="forgot-password-back-to-login-button">Back to Login</Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ForgotPassword;
